import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronsUpDown, X, Search, Check } from 'lucide-react';
import useClickOutside from 'src/hooks/useClickOutside';

export function MultiSelectDropdown({
  options,
  placeholder = 'Select options...',
  onChange,
  maxDisplay = 3,
  value, // Add value prop to control the component
  id, // <-- accept id
  className,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Use value prop instead of internal state for selected options
  const selectedOptions = value || [];

  useClickOutside([dropdownRef, menuRef], () => setIsOpen(false), isOpen);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Calculate menu position when opening and update on scroll/resize
  const updateMenuPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      // getBoundingClientRect() returns viewport coordinates, and fixed positioning is also viewport-relative
      setMenuPosition({
        top: rect.bottom + 4, // 4px offset (mt-1)
        left: rect.left,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();

      // Update position on scroll or resize
      window.addEventListener('scroll', updateMenuPosition, true); // Use capture phase to catch modal scrolls
      window.addEventListener('resize', updateMenuPosition);

      return () => {
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
      };
    }
  }, [isOpen]);

  const toggleOption = (option) => {
    const newSelection = selectedOptions.some(
      (item) => item.value === option.value
    )
      ? selectedOptions.filter((item) => item.value !== option.value)
      : [...selectedOptions, option];

    onChange(newSelection);
  };

  const removeOption = (option) => {
    onChange(selectedOptions.filter((item) => item.value !== option.value));
  };

  const handleKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        id={id} // <-- add this line
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => handleKeyDown(e, () => setIsOpen(!isOpen))}
        className={`flex items-center justify-between w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer ${className}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex flex-wrap gap-1">
          {selectedOptions.length === 0 && (
            <span className={`text-muted-foreground ${className}`}>
              {placeholder}
            </span>
          )}
          {selectedOptions.slice(0, maxDisplay).map((option) => (
            <div
              key={option.value}
              className={`flex items-center bg-[#F1F5F9] text-gray-900 rounded-full px-2 py-0.5 text-xs ${className}`}
            >
              {option.label}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeOption(option);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  handleKeyDown(e, () => removeOption(option));
                }}
                className={`ml-1 text-secondary-foreground hover:text-primary cursor-pointer ${className}`}
                aria-label={`Remove ${option.label}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {selectedOptions.length > maxDisplay && (
            <span className={`text-xs text-muted-foreground ${className}`}>
              +{selectedOptions.length - maxDisplay} more
            </span>
          )}
        </div>
        <ChevronsUpDown className={`h-4 w-4 opacity-50 ${className}`} />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={`fixed rounded-md border border-gray-200 bg-popover text-popover-foreground shadow-md z-[9999] ${className}`}
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: `${menuPosition.width}px`,
            }}
            role="listbox"
            aria-label="Options list"
          >
            <div className="flex items-center border-b border-gray-200 px-3 py-2">
              <Search
                className={`mr-2 h-4 w-4 shrink-0 opacity-50 ${className}`}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`flex h-9 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
                aria-label="Search options"
              />
            </div>
            <div className="max-h-[300px] overflow-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.value}
                  role="option"
                  tabIndex={0}
                  aria-selected={selectedOptions.some(
                    (item) => item.value === option.value
                  )}
                  onClick={() => toggleOption(option)}
                  onKeyDown={(e) =>
                    handleKeyDown(e, () => toggleOption(option))
                  }
                  className={`relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ${className}`}
                >
                  <div className="mr-2 h-4 w-4 rounded-sm border border-ah-brand-primary-form flex items-center justify-center">
                    {selectedOptions.some(
                      (item) => item.value === option.value
                    ) && (
                      <Check className="h-3 w-3 text-ah-brand-primary-form" />
                    )}
                  </div>
                  <span>{option.label}</span>
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className={`py-6 text-center text-sm ${className}`}>
                  No results found.
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
